import { useState, useEffect } from 'react';

const API = '/api';

// ── Auth helpers ──────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('gaitabot_token');
const setToken = t => localStorage.setItem('gaitabot_token', t);
const clearToken = () => localStorage.removeItem('gaitabot_token');

// ── Login Page ────────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Login failed');
      setToken(d.token);
      onLogin(d.username);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const cs = {
    page:  { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#0d1117' },
    card:  { background:'#161b22', border:'1px solid #21262d', borderRadius:16, padding:40, width:360 },
    logo:  { display:'flex', alignItems:'center', gap:12, marginBottom:28, justifyContent:'center' },
    icon:  { width:44, height:44, borderRadius:11, background:'linear-gradient(135deg,#00d4aa,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 },
    title: { fontSize:22, fontWeight:700, color:'#e2e8f0' },
    sub:   { fontSize:12, color:'#64748b', marginTop:2 },
    label: { display:'block', fontSize:11, fontWeight:600, color:'#00d4aa', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 },
    input: { width:'100%', background:'#0d1117', border:'1px solid #21262d', borderRadius:8, padding:'10px 12px', color:'#e2e8f0', fontSize:14, outline:'none', marginBottom:16 },
    btn:   { width:'100%', padding:11, borderRadius:8, border:'none', background:'linear-gradient(135deg,#00d4aa,#3b82f6)', color:'white', fontWeight:700, fontSize:14, cursor:'pointer', marginTop:4 },
    err:   { background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:7, padding:'8px 12px', color:'#fca5a5', fontSize:12, marginBottom:16 },
  };

  return (
    <div style={cs.page}>
      <div style={cs.card}>
        <div style={cs.logo}>
          <div style={cs.icon}>🦿</div>
          <div>
            <div style={cs.title}>Gaita<span style={{color:'#00d4aa'}}>Bot</span></div>
            <div style={cs.sub}>3D Gait Analysis · Swinburne University</div>
          </div>
        </div>
        <form onSubmit={submit}>
          {error && <div style={cs.err}>{error}</div>}
          <label style={cs.label}>Username</label>
          <input style={cs.input} value={username} onChange={e=>setUsername(e.target.value)}
            autoComplete="username" required />
          <label style={cs.label}>Password</label>
          <input style={cs.input} type="password" value={password} onChange={e=>setPassword(e.target.value)}
            autoComplete="current-password" required />
          <button style={cs.btn} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Main GaitaBot App ─────────────────────────────────────────────────────────
function GaitaBotApp({ username, onLogout }) {
  const [files,   setFiles]   = useState({ norm:null, kin:null, ts:null, gps:null });
  const [gender,  setGender]  = useState('Male');
  const [age,     setAge]     = useState('');
  const [name,    setName]    = useState('');
  const [busy,    setBusy]    = useState(false);
  const [status,  setStatus]  = useState('');
  const [error,   setError]   = useState('');
  const [done,    setDone]    = useState(false);

  const setFile = (k, f) => setFiles(p => ({ ...p, [k]: f }));
  const canGo   = files.kin && files.ts && !busy;

  const reset = () => {
    setFiles({ norm:null, kin:null, ts:null, gps:null });
    setDone(false); setError(''); setStatus('');
    document.querySelectorAll('input[type="file"]').forEach(el => el.value = '');
  };

  const generate = async () => {
    setBusy(true); setDone(false); setError(''); setStatus('Uploading CSV files...');
    try {
      const fd = new FormData();
      fd.append('gender', gender);
      fd.append('age',    age);
      fd.append('patientName', name);
      if (files.norm) fd.append('normCSV', files.norm);
      if (files.kin)  fd.append('kinCSV',  files.kin);
      if (files.ts)   fd.append('tsCSV',   files.ts);
      if (files.gps)  fd.append('gpsCSV',  files.gps);

      setStatus('Analysing kinematics and generating report...');
      const r = await fetch(`${API}/report/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: fd,
      });

      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `Server error ${r.status}`);
      }

      setStatus('Preparing Word document...');
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `GaitaBot_Report_${name||'Patient'}_${new Date().toISOString().slice(0,10)}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDone(true);
      setStatus('');
    } catch (err) {
      setError(err.message);
      setStatus('');
    }
    setBusy(false);
  };

  const cs = {
    page:  { minHeight:'100vh', background:'#0d1117', color:'#e2e8f0', fontFamily:"'Segoe UI',sans-serif" },
    hdr:   { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 24px', borderBottom:'1px solid #21262d', background:'#161b22' },
    logo:  { display:'flex', alignItems:'center', gap:10 },
    icon:  { width:34, height:34, borderRadius:8, background:'linear-gradient(135deg,#00d4aa,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17 },
    main:  { maxWidth:680, margin:'0 auto', padding:'28px 20px' },
    card:  { background:'#161b22', border:'1px solid #21262d', borderRadius:12, padding:20, marginBottom:14 },
    lbl:   { fontSize:10, fontWeight:700, color:'#00d4aa', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8, display:'block' },
    inp:   { width:'100%', background:'#0d1117', border:'1px solid #21262d', borderRadius:7, padding:'8px 10px', color:'#e2e8f0', fontSize:13, outline:'none', boxSizing:'border-box' },
    row:   { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 },
    fileRow: { display:'flex', alignItems:'center', gap:10, marginBottom:8 },
    fileLbl: ok => ({ fontSize:11, color:ok?'#00d4aa':'#94a3b8', minWidth:130 }),
    btn:   on => ({ width:'100%', padding:10, borderRadius:8, border:'none', background:on?'linear-gradient(135deg,#00d4aa,#3b82f6)':'#21262d', color:on?'white':'#64748b', fontWeight:700, fontSize:13, cursor:on?'pointer':'not-allowed', marginTop:4 }),
    btnSm: { width:'100%', padding:8, borderRadius:7, border:'1px solid #21262d', background:'transparent', color:'#64748b', fontSize:12, cursor:'pointer', marginTop:6 },
    err:   { background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:8, padding:'10px 14px', color:'#fca5a5', fontSize:12, marginTop:10 },
    ok:    { background:'rgba(0,212,170,0.08)', border:'1px solid rgba(0,212,170,0.25)', borderRadius:8, padding:'14px 18px', marginTop:10 },
    status:{ background:'rgba(0,212,170,0.06)', border:'1px solid rgba(0,212,170,0.18)', borderRadius:7, padding:'8px 12px', color:'#00d4aa', fontSize:12, marginTop:10 },
  };

  const FileRow = ({ label, fkey, required }) => (
    <div style={cs.fileRow}>
      <span style={cs.fileLbl(!!files[fkey])}>{required?'①':' ○'} {label} {files[fkey]?'✅':''}</span>
      <input type="file" accept=".csv" style={{fontSize:11,color:'#64748b',flex:1}}
        onChange={e => setFile(fkey, e.target.files[0]||null)} />
    </div>
  );

  return (
    <div style={cs.page}>
      {/* Header */}
      <div style={cs.hdr}>
        <div style={cs.logo}>
          <div style={cs.icon}>🦿</div>
          <div>
            <span style={{fontWeight:700,fontSize:16}}>Gaita<span style={{color:'#00d4aa'}}>Bot</span></span>
            <span style={{fontSize:11,color:'#64748b',marginLeft:8}}>3D Gait Analysis Report Generator</span>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <span style={{fontSize:12,color:'#64748b'}}>👤 {username}</span>
          <button onClick={onLogout}
            style={{padding:'5px 12px',borderRadius:6,border:'1px solid #21262d',background:'transparent',color:'#64748b',fontSize:11,cursor:'pointer'}}>
            Sign out
          </button>
        </div>
      </div>

      <div style={cs.main}>
        {/* Patient details */}
        <div style={cs.card}>
          <span style={cs.lbl}>Patient Details</span>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:11,color:'#64748b',display:'block',marginBottom:4}}>Patient Name (optional — for filename)</label>
            <input style={cs.inp} value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. J. Smith" />
          </div>
          <div style={cs.row}>
            <div>
              <label style={{fontSize:11,color:'#64748b',display:'block',marginBottom:4}}>Age</label>
              <input style={cs.inp} value={age} onChange={e=>setAge(e.target.value)} placeholder="e.g. 72" />
            </div>
            <div>
              <label style={{fontSize:11,color:'#64748b',display:'block',marginBottom:4}}>Gender</label>
              <select style={cs.inp} value={gender} onChange={e=>setGender(e.target.value)}>
                <option>Male</option><option>Female</option>
              </select>
            </div>
          </div>
        </div>

        {/* File uploads */}
        <div style={cs.card}>
          <span style={cs.lbl}>Upload CSV Files</span>
          <FileRow label="Normative data"  fkey="norm" required={false} />
          <div style={{fontSize:10,color:'#3b3f4a',marginBottom:8,marginLeft:140}}>optional — uses embedded lab norms if not uploaded</div>
          <FileRow label="Kinematics ①"    fkey="kin"  required={true} />
          <FileRow label="Tempo-Spatial ①" fkey="ts"   required={true} />
          <FileRow label="GPS data"         fkey="gps"  required={false} />
          <div style={{fontSize:10,color:'#3b3f4a',marginTop:4,marginLeft:140}}>optional — calculated from kinematics if not uploaded</div>
          <div style={{fontSize:10,color:'#64748b',marginTop:8}}>① = required</div>
        </div>

        {/* Generate button */}
        <button style={cs.btn(canGo)} onClick={generate} disabled={!canGo}>
          {busy ? '⏳ Generating report...' : '📄 Generate & Download Word Report'}
        </button>

        {done && (
          <button style={cs.btnSm} onClick={reset}>🔄 New Report</button>
        )}

        {/* Status */}
        {status && <div style={cs.status}>⏳ {status}</div>}

        {/* Error */}
        {error && <div style={cs.err}>⚠️ {error}</div>}

        {/* Success */}
        {done && !error && (
          <div style={cs.ok}>
            <div style={{color:'#00d4aa',fontWeight:700,marginBottom:4}}>✅ Report downloaded successfully</div>
            <div style={{fontSize:12,color:'#64748b',lineHeight:1.7}}>
              Your Word document has been downloaded. Open it in Microsoft Word or LibreOffice to review and edit the patient details and report content.
            </div>
          </div>
        )}

        {/* Info */}
        {!busy && !done && (
          <div style={{marginTop:24,padding:16,background:'#161b22',borderRadius:10,border:'1px solid #21262d'}}>
            <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Report Contents</div>
            <div style={{fontSize:12,color:'#64748b',lineHeight:2}}>
              📄 Page 1 — Patient details + Summary Findings + Recommendations<br/>
              📊 Page 2 — GPS bar chart + data table + literature comparison<br/>
              📈 Page 3 — Kinematic graphs (12) + Kinematic Features list<br/>
              ⏱️ Page 4 — Tempo-Spatial illustration + Gait Cycle Parameters
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) { setChecking(false); return; }
    fetch(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.valid) setUser(d.user.username); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const handleLogin  = (u) => setUser(u);
  const handleLogout = () => { clearToken(); setUser(null); };

  if (checking) return (
    <div style={{minHeight:'100vh',background:'#0d1117',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#00d4aa',fontSize:14}}>Loading GaitaBot...</div>
    </div>
  );

  if (!user) return <LoginPage onLogin={handleLogin} />;
  return <GaitaBotApp username={user} onLogout={handleLogout} />;
}
